/**
 * Boots analytics for the signed-in session.
 *
 * Sits inside AuthProvider (it needs the user to identify) and outside
 * RouterProvider (route tracking happens in AppShell via usePageTracking).
 *
 * Everything here is best-effort: a missing endpoint, a 403, a blocked SDK —
 * all resolve to "analytics is off" and the app carries on. Instrumentation is
 * never allowed to be the reason a page fails to render.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";

import { apiFetch } from "@/lib/api";
import { useAuthState } from "@/lib/auth";

import { identify, reset, start, type AnalyticsConfig } from "./client";

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthState();

  const { data: config } = useQuery({
    queryKey: ["posthog", "config"],
    queryFn: () =>
      apiFetch<AnalyticsConfig>("/posthog/config", { skipAuthRedirect: true }),
    // The key doesn't change within a session, and a backend that doesn't have
    // the endpoint yet shouldn't be retried against.
    staleTime: Infinity,
    retry: false,
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (!config || !user) return;
    let cancelled = false;
    void start(config).then((client) => {
      if (client && !cancelled) identify(user);
    });
    return () => {
      cancelled = true;
    };
  }, [config, user]);

  // Sign-out: drop the identity so the next person on this browser starts
  // a distinct session rather than inheriting the last one's.
  useEffect(() => {
    if (!user) reset();
  }, [user]);

  return <>{children}</>;
}
